// Phần "Dòng chảy khách hàng" trên tổng quan: 3 điểm bàn giao giữa các bộ phận + dải giai đoạn S0 → S8.
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { DEPT, fmtD, fmtRate, type Range, type Snapshot } from './insights';
import { handoffs, lifecycleStages, type Handoff, type Health, type Stage } from './flow';
import { Delta, Panel } from './ui';

const num = (v: number) => Math.round(v).toLocaleString('vi-VN');

const HEALTH: Record<Health, { label: string; cls: string; bar: string }> = {
  good: { label: 'Tốt', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', bar: '#1D9E75' },
  warn: { label: 'Cần theo dõi', cls: 'bg-amber-50 text-amber-700 border-amber-200', bar: '#EF9F27' },
  bad: { label: 'Tắc nghẽn', cls: 'bg-red-50 text-red-600 border-red-200', bar: '#E24B4A' },
  none: { label: 'Chưa có dữ liệu', cls: 'bg-slate-50 text-slate-500 border-slate-200', bar: '#CBD5E1' },
};

/** Đường dẫn danh sách khách hàng lọc theo thẻ và kỳ đang xem */
export const customerUrl = (tagId: number, range: Range) => `/customer?${new URLSearchParams({ tag: String(tagId), from: fmtD(range.start), to: fmtD(range.end) }).toString()}`;

const DeptDot = ({ d }: { d: Handoff['from'] | Handoff['to'] }) =>
  d === 'done'
    ? <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[11px] font-semibold text-white">Hoàn tất</span>
    : <span className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-white" style={{ background: DEPT[d].color }}>{DEPT[d].label}</span>;

function HandoffCard({ h, range }: { h: Handoff; range: Range }) {
  const H = HEALTH[h.health];
  const pct = h.input > 0 ? Math.min(100, (h.output / h.input) * 100) : 0;
  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-3.5">
      <div className="mb-2 flex items-center gap-1.5">
        <DeptDot d={h.from} /><ArrowRight size={14} className="text-slate-400" /><DeptDot d={h.to} />
        <span className={`ml-auto rounded-full border px-2 py-0.5 text-[11px] font-medium ${H.cls}`}>{H.label}</span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-3xl font-bold text-slate-800">{fmtRate(h.rate)}</span>
        <Delta cur={h.rate} prev={h.prevRate} kind="points" />
      </div>
      <div className="text-xs text-slate-500">{h.outputLabel} / {h.inputLabel}</div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: H.bar }} />
      </div>
      <div className="mt-1.5 flex justify-between text-xs text-slate-500">
        <span><b className="text-slate-800">{num(h.output)}</b> {h.outputLabel}</span>
        <span>{num(h.input)} {h.inputLabel}</span>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-[13px]">
        <span className="text-slate-600">Còn tồn: <b className={h.backlog > 0 && h.health !== 'good' ? 'text-red-500' : 'text-slate-800'}>{num(h.backlog)}</b> {h.backlogLabel}</span>
        {h.tagId && h.backlog > 0 && (
          <Link to={customerUrl(h.tagId, range)} className="flex shrink-0 items-center gap-1 text-xs font-medium text-[#357f96] hover:underline">
            Xem danh sách <ExternalLink size={12} />
          </Link>
        )}
      </div>
      {h.note && <div className="mt-2 text-xs text-amber-700">{h.note}</div>}
    </div>
  );
}

const LANES = ['page', 'tele', 'sale'] as const;
const LANE_RANGE: Record<(typeof LANES)[number], string> = { page: 'S0 – S4', tele: 'S5 – S6', sale: 'S7 – S8' };
// "S5.1 - Khách đặt lịch chưa tới", "S.8 - Hoàn Thành" → mã + tên
const STAGE_CODE = /^\s*S\s*\.?\s*[\d.]*\d/i;

/** Một dòng giai đoạn: mã, tên đầy đủ, thanh tỷ lệ, số khách; bấm để mở danh sách khách */
function StageRow({ s, max, range }: { s: Stage; max: number; range: Range }) {
  const code = s.label.match(STAGE_CODE)?.[0].replace(/\s+/g, '') ?? `S${s.no}`;
  const name = s.label.replace(STAGE_CODE, '').replace(/^\s*-?\s*/, '') || s.label;
  const color = DEPT[s.owner].color;
  const body = (
    <div className="flex items-center gap-2 px-3 py-1.5 text-[13px]">
      <span className="w-10 shrink-0 text-[11px] font-bold" style={{ color }}>{code}</span>
      <span className={`min-w-0 flex-1 truncate ${s.count ? 'text-slate-700' : 'text-slate-400'}`}>{name}</span>
      {/* 3 cột trên desktop nhỏ (lg) hẹp → nhường chỗ cho tên giai đoạn, ẩn thanh tỷ lệ */}
      <span className="hidden h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-slate-100 sm:block lg:hidden xl:block">
        <span className="block h-full rounded-full" style={{ width: `${(s.count / max) * 100}%`, background: color }} />
      </span>
      <span className={`w-8 shrink-0 text-right ${s.count ? 'font-semibold text-slate-800' : 'text-slate-300'}`}>{num(s.count)}</span>
    </div>
  );
  return (
    <Tooltip title={s.desc ? `${s.label} — ${s.desc}` : s.label} placement="topLeft" mouseEnterDelay={0.4}>
      <li>{s.tagId ? <Link to={customerUrl(s.tagId, range)} className="block hover:bg-slate-50">{body}</Link> : body}</li>
    </Tooltip>
  );
}

export function FlowSection({ cur, prev, range }: { cur: Snapshot; prev: Snapshot; range: Range }) {
  const list = handoffs(cur, prev);
  const stages = lifecycleStages(cur);
  const max = Math.max(1, ...stages.map(s => s.count));
  const worst = [...list].filter(h => h.health === 'bad' || h.health === 'warn').sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0))[0];

  return (
    <Panel
      className="mb-6" title="Dòng chảy & bàn giao khách hàng"
      info="Mỗi điểm bàn giao cho biết bộ phận nhận đã xử lý bao nhiêu khách được giao và còn tồn bao nhiêu. Dải giai đoạn S0 → S8 ghép từ thẻ khách của Trực page và Telesale; bấm một giai đoạn để mở danh sách khách."
      extra={worst ? <span className="text-xs font-medium text-red-500">Điểm cần xử lý: {worst.title}</span> : undefined}
    >
      <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
        {list.map(h => <HandoffCard key={h.key} h={h} range={range} />)}
      </div>

      {stages.length > 0 && (
        <>
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 text-xs">
            <span className="font-semibold uppercase tracking-wide text-slate-400">Khách theo giai đoạn</span>
            <span className="text-slate-400">Bấm một giai đoạn để mở danh sách khách</span>
          </div>
          {/* 3 cột theo bộ phận phụ trách, thẳng hàng với 3 điểm bàn giao phía trên */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {LANES.map(d => {
              const rows = stages.filter(s => s.owner === d);
              const total = rows.reduce((sum, s) => sum + s.count, 0);
              return (
                <div key={d} className="overflow-hidden rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 px-3 py-2" style={{ background: DEPT[d].soft }}>
                    <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: DEPT[d].color }} />
                    <span className="text-[13px] font-semibold text-slate-800">{DEPT[d].label}</span>
                    <span className="text-xs text-slate-400">{LANE_RANGE[d]}</span>
                    <span className="ml-auto text-xs text-slate-500"><b className="text-sm text-slate-800">{num(total)}</b> khách</span>
                  </div>
                  {rows.length ? (
                    <ul className="divide-y divide-slate-100">
                      {rows.map(s => <StageRow key={s.key} s={s} max={max} range={range} />)}
                    </ul>
                  ) : <div className="py-4 text-center text-xs text-slate-400">Chưa có thẻ giai đoạn</div>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Panel>
  );
}
